import type { TextItem } from './types'

export type Line = {
  page: number
  y: number
  xStart: number
  text: string
  items: TextItem[]
  bold: boolean
  fontSize: number
}

// pdfjs y grows upward, so a larger y is higher on the page. Items within this
// many units of each other are treated as the same visual line.
const Y_TOLERANCE = 4

export function groupLines(items: TextItem[]): Line[] {
  const sorted = [...items].sort((a, b) => {
    if (a.page !== b.page) return a.page - b.page
    if (Math.abs(a.y - b.y) > Y_TOLERANCE) return b.y - a.y
    return a.x - b.x
  })

  const lines: Line[] = []
  let current: TextItem[] = []

  const flush = () => {
    if (current.length === 0) return
    const ordered = [...current].sort((a, b) => a.x - b.x)
    const text = ordered
      .map((item) => item.str)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (text) {
      // A line counts as bold when the majority of its characters are bold,
      // so a stray bold glyph does not mark a whole body line as a header.
      const boldChars = ordered
        .filter((item) => item.bold)
        .reduce((sum, item) => sum + item.str.trim().length, 0)
      const totalCharsInLine = ordered.reduce(
        (sum, item) => sum + item.str.trim().length,
        0,
      )
      lines.push({
        page: ordered[0].page,
        y: ordered[0].y,
        xStart: ordered[0].x,
        text,
        items: ordered,
        bold: totalCharsInLine > 0 && boldChars / totalCharsInLine >= 0.6,
        fontSize: Math.max(...ordered.map((item) => item.height)),
      })
    }
    current = []
  }

  for (const item of sorted) {
    if (current.length === 0) {
      current.push(item)
      continue
    }
    const last = current[current.length - 1]
    const sameLine =
      item.page === last.page && Math.abs(item.y - last.y) <= Y_TOLERANCE
    if (sameLine) {
      current.push(item)
    } else {
      flush()
      current.push(item)
    }
  }
  flush()

  return lines
}

// A two-column resume renders left- and right-column text at the same vertical
// position, producing a large horizontal gap inside a single line. If a notable
// fraction of lines contain such a gap, treat the layout as multi-column
// (ATS-unfriendly).
export function detectMultiColumn(lines: Line[], pageWidth: number): boolean {
  if (pageWidth <= 0 || lines.length < 6) return false

  const gapThreshold = pageWidth * 0.25
  let splitLines = 0
  let consideredLines = 0

  for (const line of lines) {
    if (line.items.length < 2) continue
    consideredLines += 1
    let maxGap = 0
    for (let i = 1; i < line.items.length; i += 1) {
      const prev = line.items[i - 1]
      const curr = line.items[i]
      const gap = curr.x - (prev.x + prev.width)
      if (gap > maxGap) maxGap = gap
    }
    if (maxGap > gapThreshold) splitLines += 1
  }

  if (consideredLines < 6) return false
  return splitLines / consideredLines > 0.25
}
